/**
 * Firebase Index Error Handling
 * 
 * Utilities to detect and handle Firebase composite index errors
 */

import { FirebaseError } from 'firebase/app';

export interface IndexErrorInfo {
  isIndexError: boolean;
  indexUrl?: string;
  errorMessage?: string;
}

/**
 * Check if an error is a Firebase index error and extract the index creation URL
 */
export function parseFirebaseIndexError(error: unknown): IndexErrorInfo {
  if (!error || typeof error !== 'object') {
    return { isIndexError: false };
  }

  const firebaseError = error as FirebaseError;
  
  // Check if it's a Firebase error with the index message
  if (
    firebaseError.code === 'failed-precondition' ||
    (firebaseError.message && firebaseError.message.includes('index'))
  ) {
    const message = firebaseError.message || '';
    
    // Extract the URL from the error message
    const urlMatch = message.match(/https:\/\/console\.firebase\.google\.com[^\s]+/);
    
    if (urlMatch) {
      return {
        isIndexError: true,
        indexUrl: urlMatch[0],
        errorMessage: message
      };
    }
    
    // It's an index error but we couldn't extract the URL
    if (message.includes('index') || message.includes('create it here')) {
      return {
        isIndexError: true,
        errorMessage: message
      };
    }
  }
  
  return { isIndexError: false };
}

/**
 * Extract all index URLs from an error message
 */
export function extractIndexUrls(errorMessage: string): string[] {
  const urlRegex = /https:\/\/console\.firebase\.google\.com[^\s\)]+/g;
  const matches = errorMessage.match(urlRegex);
  return matches || [];
}
